import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { faClipboard } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomComponent } from 'antd/es/_util/type';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { Study, StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExMenu } from 'jf/components/DevExMenu';
import { DevExPopover } from 'jf/components/DevExPopover';
import { DevExTable, DevExTableColumn } from 'jf/components/DevExTable';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { useStudyCreator } from '../studyBuilder/useStudyCreator';

import { DeleteStudyModal } from './DeleteStudyModal';
import { RenameStudyModal } from './RenameStudyModal';
import { StudyStatusTag } from './StudyStatusTag';

const styles = {
  row: css`
    background-color: ${(props) => props.theme.color.card.background.default};
    box-shadow: 0 0 0 1px ${(props) => props.theme.color.border.secondary};
    border-radius: ${(props) => props.theme.variable.borderRadius};
    padding: ${(props) => props.theme.variable.spacing.sm};
    cursor: pointer;

    &:not(.row--disabled) {
      &:hover,
      &:focus {
        box-shadow: 0 0 0 2px ${(props) => props.theme.color.border.hovered};
        outline: none;
      }
    }

    &.row--disabled {
      opacity: ${(props) => props.theme.variable.opacity.disabled};
      cursor: not-allowed;
    }

    &:not(:last-child) {
      padding-bottom: ${(props) => props.theme.variable.spacing.sm};
    }

    td {
      padding: ${(props) => props.theme.variable.spacing.md} !important;
      background-color: transparent !important;
    }
  `,
};

export const enum StudiesTableFilter {
  ALL = 'ALL',
  USER = 'USER',
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export const STUDIES_TABLE_FILTER_OPTIONS = [
  {
    key: StudiesTableFilter.ALL,
    label: 'All',
  },
  {
    key: StudiesTableFilter.USER,
    label: 'Created by me',
  },
  {
    key: StudiesTableFilter.DRAFT,
    label: 'Draft',
  },
  {
    key: StudiesTableFilter.OPEN,
    label: 'Open',
  },
  {
    key: StudiesTableFilter.CLOSED,
    label: 'Closed',
  },
];

type StudiesTableColumn = 'name' | 'created_by' | 'last_updated' | 'status' | 'actions';

type StudiesTableProps = {
  filter: StudiesTableFilter;
  onRowClick?: (study: Study) => void;
  columns?: StudiesTableColumn[];
  isStudyDisabled?: (study: Study) => boolean;
  disabledRowTooltipText?: string;
};

export const StudiesTable: React.FC<StudiesTableProps> = (props) => {
  const theme = useDevExTheme();
  const renameStudyModal = useJFOverlay(RenameStudyModal);
  const deleteStudyModal = useJFOverlay(DeleteStudyModal);
  const { data: studies } = useClientQuery(StudyClient.getStudies);
  const studyCreator = useStudyCreator();

  const filteredStudies = useMemo(() => {
    switch (props.filter) {
      case StudiesTableFilter.USER:
        return studies?.filter((study) => study.createdBy?.ref === window.dx.user?.ref);
      case StudiesTableFilter.DRAFT:
        return studies?.filter((study) => study.status === 'DRAFT');
      case StudiesTableFilter.OPEN:
        return studies?.filter((study) => study.status === 'OPEN');
      case StudiesTableFilter.CLOSED:
        return studies?.filter((study) => study.status === 'CLOSED');
      case StudiesTableFilter.ALL:
      default:
        return studies;
    }
  }, [props.filter, studies]);

  const columnNames = props.columns;
  const defaultColumns: DevExTableColumn<Study>[] = [
    {
      key: 'name',
      title: 'Name',
      render: (study) => (
        <div
          title={study.name}
          style={{
            fontSize: theme.variable.fontSize.md,
            maxWidth: '360px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 600,
          }}
          data-cy="study-table-row-name"
        >
          <Tooltip title={study.name}>{study.name}</Tooltip>
        </div>
      ),
      width: '240px',
    },
    {
      key: 'status',
      title: 'Status',
      render: (study) => <StudyStatusTag status={study.status} />,
      width: '80px',
    },
    {
      key: 'last_updated',
      title: 'Last updated',
      render: (study) => DateTime.fromISO(study.lastUpdated).toLocaleString(DateTime.DATE_MED),
      width: '80px',
    },
    {
      key: 'created_by',
      title: 'Created by',
      render: (study) => (
        <div
          css={css`
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          {study.createdBy?.name}
        </div>
      ),
      width: '160px',
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (study) => (
        <DevExPopover
          trigger={['click']}
          content={
            <DevExMenu
              items={[
                {
                  key: 'rename',
                  label: 'Rename',
                  onClick: () => {
                    renameStudyModal.open({
                      studyName: study.name,
                      studyRef: study.ref,
                    });
                    trackEvent('survey-list:survey-actions:start-rename', { surveyRef: study.ref });
                  },
                },
                {
                  key: 'duplicate',
                  label: 'Duplicate',
                  onClick: () => {
                    trackEvent('survey-list:survey-actions:duplicate', { surveyRef: study.ref });
                    studyCreator.duplicate(study.ref);
                  },
                },
                {
                  type: 'divider',
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  danger: true,
                  onClick: () => {
                    deleteStudyModal.open({
                      studyRef: study.ref,
                      studyName: study.name,
                    });
                    trackEvent('survey-list:survey-actions:start-delete', { surveyRef: study.ref });
                  },
                },
              ]}
              selectable={false}
            />
          }
        >
          <DevExButton
            icon={<FontAwesomeIcon icon={faEllipsis} />}
            onClick={(event) => {
              event.stopPropagation();
              trackEvent('survey-list:survey-actions:open', { surveyRef: study.ref });
            }}
            type="text"
            // GBAC RESTRICTION
            // only customer admins can update/delete studies
            adminRequired
          />
        </DevExPopover>
      ),
      width: '32px',
    },
  ];

  const columns = columnNames
    ? defaultColumns.filter((column) => columnNames.includes(column.key! as StudiesTableColumn))
    : defaultColumns;

  const StudyTableRow: CustomComponent = (rowProps) => {
    const ref = rowProps['data-row-key'];

    // empty state row
    if (!ref) {
      return <>{rowProps.children}</>;
    }

    const index = filteredStudies!.findIndex((study) => study.ref === ref);
    const study = filteredStudies![index];
    const isDisabled = props.isStudyDisabled && props.isStudyDisabled(study);
    const className = classNames({
      'row--disabled': isDisabled,
    });
    return (
      <>
        {
          // use empty row to add gaps between actual rows
          index > 0 && <tr style={{ height: theme.variable.spacing.md }} />
        }
        <Tooltip
          title={isDisabled && props.disabledRowTooltipText ? props.disabledRowTooltipText : ''}
        >
          <tr
            onClick={() => {
              if (!isDisabled) {
                props.onRowClick?.(study);
              }
            }}
            css={styles.row}
            className={className}
            tabIndex={0}
            data-cy="study-table-row"
          >
            {rowProps.children}
          </tr>
        </Tooltip>
      </>
    );
  };

  return (
    <DevExTable
      columns={columns}
      dataSource={filteredStudies}
      rowKey="ref"
      components={{ body: { row: StudyTableRow } }}
      locale={{
        emptyText: <DevExEmpty icon={faClipboard} label="No surveys match your current filter." />,
      }}
      css={css`
        th {
          padding-inline: 16px !important;
        }
      `}
    />
  );
};
